// STUDENT CHAT V2 -- IMAGE ATTACHMENT FLOW. Client-side checks that mirror the
// upstream Tutor contract exactly (tutor-attachment.constant.ts,
// create-tutor-attachment-upload-url.dto.ts): jpg/jpeg/png/webp only, at most
// 10MB, at least 1 byte (the presigned POST's own content-length-range). The
// presigned policy is the real gate -- S3 rejects anything else -- so these
// checks only spare the student a doomed upload.
//
// The type is decided from the file's own leading bytes, never from its name
// or extension, and the upload format is derived from that sniffed type so
// the Content-Type pinned by the presigned policy always matches the bytes.

export const TUTOR_IMAGE_MAX_BYTES = 10 * 1024 * 1024

/** MIME type -> the `fileFormat` the upload-URL endpoint takes. */
export const TUTOR_IMAGE_FORMATS = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
}

/** What the picker offers (the same three types the contract accepts). */
export const TUTOR_IMAGE_ACCEPT = Object.keys(TUTOR_IMAGE_FORMATS).join(',')

// P40 TR-009: a desktop picker hides .heic files unless they are listed. Only
// the extensions, and only on desktop: on iOS a HEIC entry in `accept` would
// stop Photos from handing over a JPEG.
export const TUTOR_IMAGE_ACCEPT_DESKTOP = `${TUTOR_IMAGE_ACCEPT},.heic,.heif`

// P40 TR-009: ISO-BMFF `ftyp` major brands of HEIF stills and sequences
// (HEVC-coded; AVIF is not handled).
const HEIF_BRANDS = [
  'heic',
  'heix',
  'hevc',
  'hevx',
  'heim',
  'heis',
  'hevm',
  'hevs',
  'mif1',
  'msf1',
]

/**
 * @param {Uint8Array} bytes -- the file's first 12 bytes (or more)
 * @returns {boolean} whether the file is a HEIC/HEIF picture
 */
export function isHeifHeader(bytes) {
  if (!bytes || bytes.length < 12) return false
  const text = (from, to) =>
    String.fromCharCode.apply(null, Array.from(bytes.slice(from, to)))
  return text(4, 8) === 'ftyp' && HEIF_BRANDS.includes(text(8, 12))
}

function readHead(file, length) {
  const blob = file.slice(0, length)
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(blob)
  })
}

/**
 * @param {Uint8Array} bytes
 * @returns {'image/jpeg'|'image/png'|'image/webp'|null}
 */
export function sniffImageType(bytes) {
  const at = (i, values) => values.every((v, k) => bytes[i + k] === v)
  if (bytes.length >= 3 && at(0, [0xff, 0xd8, 0xff])) return 'image/jpeg'
  if (
    bytes.length >= 8 &&
    at(0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
  ) {
    return 'image/png'
  }
  // "RIFF" <size> "WEBP"
  if (
    bytes.length >= 12 &&
    at(0, [0x52, 0x49, 0x46, 0x46]) &&
    at(8, [0x57, 0x45, 0x42, 0x50])
  ) {
    return 'image/webp'
  }
  return null
}

/**
 * @param {File|Blob} file
 * @returns {Promise<{ok: true, mime: string, fileFormat: string}
 *   | {ok: false, reason: 'empty'|'too_large'|'type'|'heic'}>}
 *   'heic': a HEIC/HEIF picture, which the caller converts (P40 TR-009) --
 *   reported before the size cap, which applies to the converted JPEG.
 */
export async function validateTutorImage(file) {
  if (!file || !file.size) return { ok: false, reason: 'empty' }
  let head = null
  try {
    head = new Uint8Array(await readHead(file, 12))
  } catch (err) {
    head = null
  }
  if (head && isHeifHeader(head)) return { ok: false, reason: 'heic' }
  if (file.size > TUTOR_IMAGE_MAX_BYTES)
    return { ok: false, reason: 'too_large' }
  const mime = head ? sniffImageType(head) : null
  if (!mime) return { ok: false, reason: 'type' }
  return { ok: true, mime, fileFormat: TUTOR_IMAGE_FORMATS[mime] }
}

export function createPreviewUrl(file) {
  return typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
    ? URL.createObjectURL(file)
    : ''
}

export function revokePreviewUrl(url) {
  if (
    url &&
    typeof URL !== 'undefined' &&
    typeof URL.revokeObjectURL === 'function'
  ) {
    URL.revokeObjectURL(url)
  }
}
